import React, { useContext, useState, useEffect, useRef } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import { Button, Inline, Separator } from '@front10/base-elements';
/**
 * Atoms
 */
import { isSSR, useIsMobileView } from '@front10/utils';
import { Listbox } from '../../lab/Listbox';
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import nanoid from 'nanoid';
import Popover from '../Popover';
import VisuallyHidden from '@reach/visually-hidden';
/**
 * Controls
 */
import InlineBudget from '../Budget/Budget';
/**
 * utils
 */
import formatMoney from '../../controls/Budget/utils';
import ReactFocusLock from 'react-focus-lock';

const TAB_KEY = 9;

const BudgetFilter = (props, ref) => {
  const [id] = React.useState(nanoid());
  const {
    basicBudget,
    alwaysEnabledDoneButton = true,
    focusLock = true,
    currencySettings = {
      USD: {
        currencyToDisplay: '$',
        currencyCode: 'USD'
      }
    },
    value: valueByProps,
    defaultCurrency = 'USD',
    decimalPlaces = 2,
    disabled,
    showLabelfilterButton,
    showLabelValueEmptyFilterButton,
    showLabelSeparatorFilterButton = false,
    titleLabelKey = 'cmp-budget-title',
    titleLabelSeparatorKey = 'cmp-budget-title-separator',
    doneButtonLabelKey = 'cmp-budget-done-button',
    clearButtonLabelKey = 'cmp-budget-clear-button',
    currencySelectorLabelKey = 'cmp-budget-currency-selector-title',
    filterButtonIconProps,
    containerSettings = {
      showSeparator: true,
      enabledDoneButton: true,
      enabledClearButton: true,
      isMobile: false
    },
    popover = {
      containerProps: {
        style: {
          position: 'relative'
        }
      }
    },
    customMobileScreenWidth = []
  } = props;

  let thousandSeparator =
    currencySettings[props?.currencyCode]?.thousandSeparator ?? props.thousandSeparator;
  let decimalSeparator =
    currencySettings[props?.currencyCode]?.decimalSeparator ?? props.decimalSeparator;
  let currencyToDisplay =
    currencySettings[props?.currencyCode]?.currencyToDisplay ?? props.currencyToDisplay;

  let isMobile = useIsMobileView({ customMobileScreenWidth });
  if (containerSettings.isMobile) {
    isMobile = containerSettings.isMobile;
  }

  const getValue = (valueByProps) => {
    if (valueByProps) {
      if (typeof valueByProps === 'string' || typeof valueByProps === 'number') {
        return valueByProps;
      } else {
        if (typeof valueByProps === 'object') {
          if (Object.keys(valueByProps).length !== 0) {
            return valueByProps?.maximum ? valueByProps?.maximum : '';
          } else {
            return '';
          }
        }
      }
    }
    return '';
  };

  const value = getValue(valueByProps);

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  function transformCurrencies() {
    const options = [];
    Object.keys(currencySettings).forEach((key) => {
      options.push({ value: key, label: translate(`cmp-budget-label-currency-${key}`, key) });
    });
    return options;
  }
  const label = translate(titleLabelKey, 'Budget');
  const options = transformCurrencies();
  const budgetRef = useRef(null);
  const popoverRef = useRef();
  const flexibleRef = useRef();
  const [budgetValue, setBudget] = useState(value);
  const [doneButtonClick, setDoneButtonClick] = useState(false);
  const [currency, setCurrency] = useState(
    Boolean(currencyToDisplay) ? currencyToDisplay : defaultCurrency
  );
  const [save, setSave] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [enabledAccessibilityText, setEnabledAccessibilityText] = useState(false);
  const filterButtonRef = useRef(null);
  const [activeDone, setActiveDone] = useState(false);

  /**
   * This update is because the component
   * clears the value but keeps the formattedValue
   */
  useEffect(() => {
    setBudget(value);
    setCurrency(Boolean(currencyToDisplay) ? currencyToDisplay : defaultCurrency);
    if (value === null) {
      budgetRef.current?.reset();
    }
  }, [value, currencyToDisplay, defaultCurrency]);

  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        if (!isSSR()) {
          document.querySelector(`#toggleElementId-${id}`).focus();
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const disabledButton = React.useMemo(() => {
    let disabled = true;
    disabled = budgetValue === null || (value === '' && budgetValue === '');
    return disabled;
  }, [value, budgetValue]);

  var timer;
  const footer = (
    <Inline variant="layout.popover.footer" gap={2} align="end" pb="11px">
      {containerSettings.enabledClearButton && (
        <Button
          disabled={disabledButton}
          variant="text"
          onClick={() => {
            setBudget(null);
            setSave(null);
            props.onChange(null);
            budgetRef.current?.reset();
            setActiveDone(true);
          }}
          {...props.popover?.footerClearButtonComponent}
        >
          {translate(clearButtonLabelKey, 'Clear')}
        </Button>
      )}

      {containerSettings.enabledDoneButton && (
        <Button
          disabled={
            alwaysEnabledDoneButton === true ? false : activeDone ? !activeDone : disabledButton
          }
          variant="text"
          onClick={() => {
            popoverRef.current.closePopover();
            filterButtonRef.current.focus();
            if (budgetValue) {
              props.onChange(budgetValue);
              setSave(budgetValue);
            }
            setDoneButtonClick(true);
          }}
          onKeyDown={(e) => {
            if (!e.shiftKey && e.keyCode === TAB_KEY && !focusLock) {
              e.preventDefault();
              popoverRef.current.closePopover();
              filterButtonRef.current.focus();
            }
          }}
          {...props.popover?.footerDoneButtonComponent}
        >
          {translate(doneButtonLabelKey, 'Done')}
        </Button>
      )}
    </Inline>
  );

  const removeA11YMessage = () => {
    setTimeout(() => {
      setEnabledAccessibilityText(false);
    }, 150);
  };

  const ParentDropdownElement = focusLock ? ReactFocusLock : React.Fragment;

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        {isMobile ? (
          <FlexibleFilterContainer
            ref={flexibleRef}
            {...containerSettings}
            mobileView={customMobileScreenWidth?.length > 0 ? isMobile : containerSettings.isMobile}
            saveOnClose={false}
            variant="layout.popover"
            label={label}
            title={label}
            disabledClearButton={disabledButton}
            disabledDoneButton={
              alwaysEnabledDoneButton === true ? false : activeDone ? !activeDone : disabledButton
            }
            customAccessibilityToggleElement={true}
            labelBottomBtn={translate(doneButtonLabelKey, 'Done')}
            labelClearBtn={translate(clearButtonLabelKey, 'Clear')}
            onClearBtn={() => {
              setBudget(null);
              setSave(null);
              props.onChange(null);
              budgetRef.current?.reset();
              setActiveDone(true);
            }}
            onDismiss={() => {
              if (value !== null && value !== budgetValue) {
                setBudget(value);
                budgetRef.current?.update(value);
              }
              if (activeDone) {
                setActiveDone(false);
              }
            }}
            onOpenPopup={() => {
              setTimeout(() => budgetRef.current.focusedElement(), 700);

              if (value === null && save !== budgetValue && save === value) {
                budgetRef.current?.update(save);
                setBudget(save);
                setSave(null);
              } else if (value !== null && value !== budgetValue) {
                setBudget(value);
                budgetRef.current?.update(value);
              }
            }}
            toggleElement={
              <FilterButton
                id={`toggleElementId-${id}`}
                value={
                  value
                    ? `${showLabelfilterButton ? label : ''} ${translate(
                        `cmp-currency-${currency}`,
                        currency
                      )} ${formatMoney(value, decimalPlaces, thousandSeparator, decimalSeparator)}`
                    : showLabelValueEmptyFilterButton
                    ? showLabelSeparatorFilterButton
                      ? `${label}${translate(titleLabelSeparatorKey, ':')} ${translate(
                          'cmp-budget-all',
                          'All'
                        )}`
                      : `${label}: ${translate('cmp-budget-all', 'All')}`
                    : label
                }
                iconSettings={filterButtonIconProps}
                aria-label={`${label} ${
                  value ? `${translate(`cmp-currency-${currency}`, currency)} ${value}` : ''
                }`}
                active={!disabled && Boolean(value)}
                disabled={disabled} // used to disabled the component
                onFocus={() => {
                  if (flexibleRef.current.isOpen) {
                    timer = setTimeout(() => {
                      flexibleRef.current.close();
                    }, 150);
                  }
                }}
                onBlur={() => {
                  clearTimeout(timer);
                }}
              />
            }
            onSave={() => {
              if (budgetValue) {
                props.onChange(budgetValue);
                setSave(budgetValue);
              }
              if (activeDone) {
                setActiveDone(false);
              }
            }}
          >
            <Panels showSeparator={false}>
              {/* {options.length > 1 && ( */}
              {false && (
                <Listbox
                  label={translate(currencySelectorLabelKey, 'Currency')}
                  layout="standard"
                  options={options}
                  key={Math.floor(Math.random() * 100 + 1)}
                  defaultValue={currency}
                  onChange={(newCurrency) => {
                    setCurrency(newCurrency);
                  }}
                />
              )}
              <InlineBudget
                {...basicBudget}
                ref={budgetRef}
                currencyCode={translate(`cmp-currency-${currency}`, currency)}
                defaultValue={value === null ? null : budgetValue}
                onChange={(newBudget) => setBudget(newBudget)}
                onClear={() => {
                  setSave(null);
                  setBudget(null);
                  props.onChange(null);
                }}
                decimalPlaces={decimalPlaces}
                currencyToDisplay={currencyToDisplay}
                thousandSeparator={thousandSeparator}
                // onBlur={() => {
                //   if (
                //     budgetRef?.current?.state.formatterValue === null ||
                //     budgetRef?.current?.state.formatterValue === ''
                //   ) {
                //     if (!isSSR()) document.querySelector(`#toggleElementId-${id}`).focus();
                //   }
                // }}
              />
            </Panels>
          </FlexibleFilterContainer>
        ) : (
          <>
            <VisuallyHidden role="status">
              {!isOpen && enabledAccessibilityText
                ? translate('cmp-budget-filte-collapsed', 'Budget filter collapsed')
                : ''}
            </VisuallyHidden>
            <Popover
              alignLeft={true}
              element={
                <FilterButton
                  id={`toggleElementId-${id}`}
                  iconSettings={filterButtonIconProps}
                  ref={filterButtonRef}
                  value={
                    value
                      ? `${showLabelfilterButton ? label : ''} ${translate(
                          `cmp-currency-${currency}`,
                          currency
                        )} ${formatMoney(
                          value,
                          decimalPlaces,
                          thousandSeparator,
                          decimalSeparator
                        )}`
                      : showLabelValueEmptyFilterButton
                      ? showLabelSeparatorFilterButton
                        ? `${label}${translate(titleLabelSeparatorKey, ':')} ${translate(
                            'cmp-budget-all',
                            'All'
                          )}`
                        : `${label}: ${translate('cmp-budget-all', 'All')}`
                      : label
                  }
                  aria-expanded={isOpen}
                  aria-haspopup="dialog"
                  aria-label={`${label} ${
                    value ? `${translate(`cmp-currency-${currency}`, currency)} ${value}` : ''
                  }`}
                  active={!disabled && Boolean(value)}
                  disabled={disabled} // used to disabled the component
                />
              }
              ref={popoverRef}
              onClose={(e) => {
                removeA11YMessage();
                setIsOpen(false);
                filterButtonRef.current.focus();
                if (!doneButtonClick && !value) {
                  setSave(null);
                  setBudget(null);
                }
              }}
              onOpen={() => {
                setIsOpen(true);
                setEnabledAccessibilityText(true);
                setTimeout(() => budgetRef.current?.focusedElement(), 500);
                if (value === null && save !== budgetValue && save === value) {
                  budgetRef.current?.update(save);
                  setBudget(save);
                  setSave(null);
                } else if (value !== null && value !== budgetValue) {
                  setBudget(value);
                  budgetRef.current?.update(value);
                }
                setDoneButtonClick(false);
                if (activeDone) {
                  setActiveDone(false);
                }
              }}
              dialogAriaLabel={translate('cmp-budget-dialog-aria-label', 'Budget')}
              {...popover}
            >
              <ParentDropdownElement autoFocus returnFocus>
                <Panels showSeparator={false}>
                  {/* {options.length > 1 && ( */}
                  {false && (
                    <Listbox
                      label={translate(currencySelectorLabelKey, 'Currency')}
                      layout="standard"
                      options={options}
                      key={Math.floor(Math.random() * 100 + 1)}
                      defaultValue={currency}
                      onChange={(newCurrency) => {
                        setCurrency(newCurrency);
                      }}
                    />
                  )}
                  <InlineBudget
                    {...basicBudget}
                    ref={budgetRef}
                    currencyCode={translate(`cmp-currency-${currency}`, currency)}
                    defaultValue={value === null ? null : budgetValue}
                    onChange={(newBudget) => setBudget(newBudget)}
                    onClear={() => {
                      setSave(null);
                      setBudget(null);
                      props.onChange(null);
                    }}
                    decimalPlaces={decimalPlaces}
                    currencyToDisplay={currencyToDisplay}
                    thousandSeparator={thousandSeparator}
                    onKeyDown={(e) => {
                      if (
                        !Boolean(budgetValue) &&
                        !Boolean(save) &&
                        e.keyCode === 9 &&
                        !alwaysEnabledDoneButton
                      ) {
                        popoverRef.current.closePopover();
                        setTimeout(() => filterButtonRef.current.focus(), 50);
                      }
                    }}
                  />
                </Panels>
                {containerSettings.showSeparator && <Separator />}
                {footer}
              </ParentDropdownElement>
            </Popover>
          </>
        )}
      </ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(BudgetFilter);
